/**
 * Add Note Dialog Component
 *
 * Dialog for adding care queue notes to a resident.
 * Features a custom teal header, note textarea, and tip section.
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useApiService } from "../../contexts/ApiServiceContext";
import type { ResidentSummary } from "../../types/residents";
import { trackCareQueueEvent, bucketLength } from "../../lib/tracking";

interface AddNoteDialogProps {
  resident: ResidentSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddNoteDialog({
  resident,
  open,
  onOpenChange,
}: AddNoteDialogProps) {
  const api = useApiService();
  const queryClient = useQueryClient();

  const [content, setContent] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Analytics: emit when dialog opens
  useEffect(() => {
    if (open) {
      trackCareQueueEvent("care_queue_dialog_opened", { dialogType: "note" });
    }
  }, [open]);

  // Reset form when dialog opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      trackCareQueueEvent("care_queue_dialog_action", {
        dialogType: "note",
        action: "cancel",
      });
      // Reset form state when closing
      setContent("");
      setError(null);
    }
    onOpenChange(newOpen);
  };

  // Mutation for creating the note
  const createNoteMutation = useMutation({
    mutationFn: async () => {
      if (!resident) throw new Error("No resident selected");
      return api.createCareQueueNote(resident.resident_id, {
        content: content.trim(),
        high_priority: false,
      }, resident.wq_id);
    },
    onSuccess: () => {
      if (resident) {
        // Invalidate care queue notes query to refresh the detail view
        queryClient.invalidateQueries({
          queryKey: ["resident-care-queue-notes", resident.resident_id],
        });
        // Invalidate residents summary so the note count badge updates immediately
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === "string" &&
            query.queryKey[0].includes("residents"),
        });
      }
      toast.success("Note added successfully");
      handleOpenChange(false);
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Failed to save note";
      setError(message);
      toast.error("Failed to add note", { description: message });
    },
  });

  const handleSubmit = () => {
    // Validate content
    if (!content.trim()) {
      setError("Note content is required");
      return;
    }
    if (content.trim().length > 5000) {
      setError("Note content must be 5000 characters or less");
      return;
    }
    setError(null);
    trackCareQueueEvent("care_queue_dialog_action", {
      dialogType: "note",
      action: "confirm",
    });
    trackCareQueueEvent("care_queue_note_added", {
      residentId: String(resident?.wq_id || resident?.resident_id),
      noteLength: bucketLength(content.trim().length),
    });
    createNoteMutation.mutate();
  };

  if (!resident) {
    return null;
  }

  // Parse resident name for display (Last, First -> First Last format for subtitle)
  const displayRoom = resident.room || "No Room";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="p-0 gap-0 overflow-hidden bg-card [&>button]:hidden"
        style={{ width: "560px", maxWidth: "calc(100vw - 2rem)" }}
      >
        {/* Custom Teal Header */}
        <div className="px-8 py-5 relative rounded-t bg-primary">
          <button
            onClick={() => handleOpenChange(false)}
            className="absolute top-5 right-5 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
            aria-label="Close"
          >
            <XCircle className="h-6 w-6" />
          </button>
          <h2 className="text-primary-foreground" style={{ fontSize: '20px', fontWeight: 700 }}>Add Note</h2>
          <p className="text-base text-primary-foreground/90 mt-1">
            {resident.resident_name} &bull; {displayRoom}
          </p>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-5 bg-card">
          {/* Note Text */}
          <div className="space-y-2">
            <label
              htmlFor="note-content"
              className="text-sm font-medium text-foreground"
            >
              Note Text:
            </label>
            <Textarea
              id="note-content"
              placeholder="Enter your note here..."
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                if (error) setError(null);
              }}
              className="min-h-[180px] resize-y"
              style={{ border: '1px solid rgba(209, 210, 219, 1.00)' }}
              aria-invalid={!!error}
            />
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
          </div>

          {/* Tip Section */}
          <div className="p-4 rounded bg-background">
            <p className="text-sm text-foreground">
              <span className="font-medium">Tip:</span> Include relevant
              observations, changes in condition, or important events.
            </p>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-border bg-muted/50">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={createNoteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createNoteMutation.isPending || !content.trim()}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {createNoteMutation.isPending ? "Saving..." : "Save Note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
